import React, { useState } from 'react';
import { useChartOverlays } from '../context/ChartOverlayContext';
import { useAuth } from '../context/AuthContext';
import { addAreaHighlightOverlaysAndMessages } from '../components/addAreaHighlightOverlaysUtil';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import HelpIcon from './HelpIcon';

/**
 * Width of the label column, shared by every row of the form.
 *
 * One constant rather than a per-row number so the dropdowns, the degree inputs and the two radio
 * groups all begin at the same x. Wide enough for the longest label, "Display Style"; the rows
 * previously used 48, which left the radio groups starting well right of the fields above them.
 */
const LABEL_COLUMN_WIDTH = 104;

/**
 * Cancels the negative left margin MUI gives FormControlLabel.
 *
 * The label column already put every row's label at the same x, but the two radio groups still did
 * not line up with the dropdowns above them: FormControlLabel ships `margin-left: -11px` so a
 * radio's ripple padding does not visibly indent its text, and that pulls the whole group left of
 * the column it was placed in. The labels agreed while the controls did not, which is the harder
 * version of the problem to see — nothing looks wrong until the eye follows the second column down.
 *
 * Applied to the first control only. The gaps BETWEEN radios are the component's own spacing and
 * are not what is misaligned.
 */
const RADIO_GROUP_SX = { ml: 0, '& .MuiFormControlLabel-root:first-of-type': { ml: 0 } };

const PLANETS = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter',
  'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'
];

const COLOR_OPTIONS = [
  'rgba(229, 57, 53, 0.55)',    // Red
  'rgba(30, 136, 229, 0.55)',   // Blue
  'rgba(67, 160, 71, 0.55)',    // Green
  'rgba(251, 192, 45, 0.55)',   // Yellow
  'rgba(142, 36, 170, 0.55)',   // Purple
  'rgba(255, 87, 34, 0.55)',    // Orange
  'rgba(120, 144, 156, 0.55)',  // Gray
  'rgba(255, 193, 7, 0.55)',    // Amber
  'rgba(233, 30, 99, 0.55)',    // Pink
  'rgba(0, 188, 212, 0.55)',    // Cyan
  'rgba(205, 220, 57, 0.55)',   // Lime
  'rgba(96, 125, 139, 0.55)',   // Blue Gray
  'rgba(255, 152, 0, 0.55)',    // Deep Orange
  'rgba(124, 179, 66, 0.55)',   // Light Green
  'rgba(255, 255, 255, 0.55)',  // White
  'rgba(0, 0, 0, 0.55)',        // Black
  'rgba(255, 234, 0, 0.55)',    // Bright Yellow
  'rgba(0, 200, 83, 0.55)',     // Vivid Green
  'rgba(213, 0, 249, 0.55)',    // Vivid Purple
  'rgba(255, 61, 0, 0.55)',     // Vivid Red
];


import PropTypes from 'prop-types';

function AspectsAreaHighlightDialog({ open, onClose, onRun, barData, extendedBarData, onJournalAppend }) {
  const { api, isAdmin } = useAuth();
  const { addOverlay } = useChartOverlays();
  // State for planet/aspect rows (must be declared first!)
  //
  // Rahu/Sun, written out rather than derived. The dialog used to seed this by running Sun/Rahu
  // through orderBySpeed, which put the slower body first and produced exactly this pair — so
  // spelling it out changes nothing on screen. Letting the raw Sun/Rahu through instead WOULD have
  // changed it: for the default 30-210 range the two orders are different studies (see below), so
  // every user's opening study would silently have become the reflected arc.
  const [rows, setRows] = useState(() => ([
    { planet1: 'Rahu', planet2: 'Sun', from: 30 },
    { planet1: 'Rahu', planet2: 'Sun', from: 210 },
  ]));

  // 'same'      row 2 mirrors row 1's bodies; only the closing degree is the user's to choose.
  // 'different' row 2 names its own pair, so the window opens on one aspect and closes on another.
  //
  // Default is 'same', which is exactly what the dialog could express before, so an existing user
  // finds the same study they had.
  const [pairMode, setPairMode] = useState('same');
  const isDifferentPair = pairMode === 'different';

  // 'bands' keeps the shaded vertical highlight; 'lines' replaces it with one line per window,
  // drawn from the opening bar's open to the closing bar's close. The report, the tables and every
  // statistic are computed from the date pairs and are untouched by this — it reaches the overlays
  // and nothing else.
  const [displayStyle, setDisplayStyle] = useState('bands');

  const handlePairModeChange = (mode) => {
    setPairMode(mode);
    if (mode === 'same') {
      // Snap row 2 back onto row 1. Leaving the independent selection in place while the dropdowns
      // are disabled would show a pair the study is not going to use — the state the disabled
      // controls describe has to be the state that runs.
      setRows(prev => prev.map((row, i) =>
        i === 1 ? { ...row, planet1: prev[0].planet1, planet2: prev[0].planet2 } : row));
    }
  };

  // Map planet names to color indices for default color selection
  const PLANET_COLOR_MAP = {
    'Sun': COLOR_OPTIONS[0],
    'Moon': COLOR_OPTIONS[1],
    'Mercury': COLOR_OPTIONS[2],
    'Venus': COLOR_OPTIONS[3],
    'Mars': COLOR_OPTIONS[4],
    'Jupiter': COLOR_OPTIONS[5],
    'Saturn': COLOR_OPTIONS[6],
    'Uranus': COLOR_OPTIONS[7],
    'Neptune': COLOR_OPTIONS[8],
    'Pluto': COLOR_OPTIONS[9],
    'Rahu': COLOR_OPTIONS[10],
    // Blue Gray: the only unused entry that stays distinct from Rahu's lime, so the two ends of
    // the nodal axis never come back the same colour.
    'Ketu': COLOR_OPTIONS[11]
  };

  const [color, setColor] = useState(PLANET_COLOR_MAP['Rahu']);

  // When planet2 in the first row changes, update color to match
  React.useEffect(() => {
    setColor(PLANET_COLOR_MAP[rows[0].planet2] || COLOR_OPTIONS[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows[0].planet2]);

  const handleRowChange = (idx, field, value) => {
    setRows(prev => {
      // Editing the opening degree pre-fills the closing one with its opposition — but only when
      // the two rows describe one pair. With independent pairs the closing degree is a separate
      // event the user chose deliberately, and silently rewriting it because they went back and
      // adjusted the opening degree would discard that choice.
      if (idx === 0 && field === 'from' && !isDifferentPair) {
        let num = parseFloat(value);
        if (isNaN(num)) num = '';
        const toVal = num === '' ? '' : ((num + 180) % 360);
        return prev.map((row, i) => {
          if (i === 0) return { ...row, from: num };
          if (i === 1) return { ...row, from: toVal };
          return row;
        });
      }

      if (field === 'planet1' || field === 'planet2') {
        // The pair is taken exactly as entered. Either body may be Planet 1.
        //
        // This used to swap so the slower body always landed in Planet 1, on the reasoning that an
        // aspect is conventionally read with the slower body as the anchor. That is true of how an
        // aspect is NAMED, and it is not true of what this study computes.
        //
        // The range decides. An ascending range bounded at or below 180 — 0-15, 0-90, 0-120 — is an
        // angular distance, which has no direction, so Moon-Saturn and Saturn-Moon are the same
        // study and the swap was invisible. Anything else is a DIRECTED arc: how far Planet 1 lies
        // ahead of Planet 2, measured forwards. There, reversing the bodies reflects the arc —
        // Saturn-Moon 30-210 is Moon-Saturn 150-330 — and the swap made one of every such pair
        // unaskable. Verified against the engine: the folded windows are identical to the day in
        // either order, and the reflected ones match their counterpart exactly.
        //
        // So the invariant is gone and nothing replaces it. The backend never reordered anything;
        // this was the only place it happened.
        const next = { ...prev[idx], [field]: value };

        return prev.map((row, i) => {
          if (i === idx) return next;
          // Row 2 mirrors row 1 only while the pairs are meant to be the same.
          if (idx === 0 && i === 1 && !isDifferentPair) {
            return { ...row, planet1: next.planet1, planet2: next.planet2 };
          }
          return row;
        });
      }

      return prev.map((row, i) => i === idx ? { ...row, [field]: value } : row);
    });
  };



  const PLANET_INDEX_MAP = {
    'Sun': 0,
    'Moon': 1,
    'Mercury': 2,
    'Venus': 3,
    'Mars': 4,
    'Jupiter': 5,
    'Saturn': 6,
    'Uranus': 7,
    'Neptune': 8,
    'Pluto': 9,
    // The TRUE node, canonical for Rahu since 2026-08-31. It was 10 — the MEAN node — and the two
    // differ by up to 1.7 degrees, so leaving it would have made this dialog quietly disagree with
    // every other study about where Rahu is.
    //
    // A numeric id means exactly that id on the wire: the backend does NOT re-read 10 as "whatever
    // Rahu means now", deliberately, so a frontend mid-deploy keeps getting the body it asked for.
    // That is why this has to change here and not only in the backend's name table.
    'Rahu': 11,
    'North Node': 11,
    // Ketu has no ephemeris body — the backend resolves this id to the node and adds 180 degrees.
    // 9998 is the antipode of the TRUE node, pairing with the Rahu above; 9999 is the mean-node
    // Ketu. Both sit far outside the body range so a missed resolution fails loudly instead of
    // silently returning the mean lunar apogee, which is what sits next to the nodes.
    'Ketu': 9998,
    'South Node': 9998
  };

  const handleRun = async () => {

  // Diagnostic: Log barData and rows before running
  console.log('[AspectsAreaHighlightDialog] handleRun called. barData:', barData);
  console.log('[AspectsAreaHighlightDialog] handleRun called. rows:', rows);
  // The bodies the study will actually run on. In same-pair mode row 2's dropdowns are disabled,
  // so its stored planets are a mirror rather than a choice — read them from row 1 so the payload
  // cannot disagree with what the dialog is showing.
  const startPlanets = { planet1: rows[0].planet1, planet2: rows[0].planet2 };
  const endPlanets = isDifferentPair
    ? { planet1: rows[1].planet1, planet2: rows[1].planet2 }
    : startPlanets;

  // Two shapes, on purpose.
  //
  // startPair/endPair is the current contract: bodies by name, one object per end of the window.
  // planet11..degrees2 repeat the same thing as indices, which is what the backend read before.
  //
  // The duplication is deliberate and cheap. The frontend ships to Firebase Hosting and the
  // backend to Cloud Run, independently, so there is always a window where one is newer than the
  // other. A payload carrying only the new shape would reach an old backend as planet index 0 for
  // everything — that is the Sun, so the study would return a plausible set of bands for bodies
  // nobody selected, with no error to notice. Sending both makes the deploy order irrelevant.
  // Drop the flat fields once the backend has been on this version for a while.
  const payload = {
    "isDifferentPair": isDifferentPair,
    "startPair": { ...startPlanets, "degree": Number(rows[0].from) },
    "endPair": { ...endPlanets, "degree": Number(rows[1].from) },
    "overlayColor": color,

    "planet11": PLANET_INDEX_MAP[startPlanets.planet1],
    "planet12": PLANET_INDEX_MAP[startPlanets.planet2],
    "degrees1": Number(rows[0].from),
    "planet21": PLANET_INDEX_MAP[endPlanets.planet1],
    "planet22": PLANET_INDEX_MAP[endPlanets.planet2],
    "degrees2": Number(rows[1].from),

    "startDateTime": (extendedBarData?.dates?.[0] || barData.dates[0]),
    "endDateTime": (extendedBarData?.dates?.[extendedBarData.dates.length - 1] || barData.dates[barData.dates.length - 1]),
    "symbol": barData.symbol,
    "color": color
  };
  console.log('[AspectsAreaHighlightDialog] Payload for backend:', payload);

  try {
    const res = await api.post('/api/area-highlight-study/planet-to-planet-aspect', payload);
    const data = res.data;
    console.log('[AspectsAreaHighlightDialog] Backend response:', data);
    // Expect: {datePairs: [...], summaryLines: [...], futureDates: [...]}
    const datePairs = Array.isArray(data) ? data : data?.datePairs || [];
    const summaryLines = data?.summaryLines || [];
    const futureDates = data?.futureDates || [];
    console.log('[AspectsAreaHighlightDialog] Passing to overlays/messages:', { datePairs, summaryLines, futureDates });
    addAreaHighlightOverlaysAndMessages({
        isAdmin,
      datePairs: datePairs || [],
      summaryLines: summaryLines, // Pass the raw data, the utility will handle slicing
      futureDates: futureDates || [],
      params: {
        color,
        displayStyle,
        // Names both ends when they differ, so two studies that share an opening pair but close on
        // different ones are still tellable apart on the chart and in the journal.
        label: isDifferentPair
          ? `${startPlanets.planet1}-${startPlanets.planet2} ${rows[0].from}° → ${endPlanets.planet1}-${endPlanets.planet2} ${rows[1].from}°`
          : `${startPlanets.planet1}-${startPlanets.planet2} ${rows[0].from}°/${rows[1].from}°`,
        planet1: startPlanets.planet1,
        planet2: startPlanets.planet2,
        degrees: rows[0].from
      },
      addOverlay,
      barData,
      extendedBarData,
      updateMessagesPanel: ({ summaryLines: processedSummary, futureDates }) => {
        if (typeof onRun === 'function') {
          const title = isDifferentPair
            ? `${startPlanets.planet1}-${startPlanets.planet2}-${rows[0].from} → ${endPlanets.planet1}-${endPlanets.planet2}-${rows[1].from}`
            : `${startPlanets.planet1}-${startPlanets.planet2}-${rows[0].from}-${rows[1].from}`;
          onRun({ stats: processedSummary, futureDates, title });
        }
      },
      onJournalAppend,
    });
  } catch (err) {
    console.error('[AspectsAreaHighlightDialog] handleRun error:', err);
  }
};

  return (
    // maxHeight raised from 380: the Planet Pair row is new, and the sign controls it replaced cost
    // width rather than height, so the old ceiling would have clipped the RUN button.
    <Dialog open={open} onClose={onClose} PaperProps={{ style: { minWidth: 900, borderRadius: 12, maxHeight: 440, padding: 0 } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.3rem', pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Aspect Area Highlight Studies</span>
        <HelpIcon helpKey="aspectsAreaHighlightDialog" />
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 1.5, paddingTop: 1.5 }}>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 500, mb: 0.5 }}>Select Overlay Color:</Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={color}
              onChange={e => setColor(e.target.value)}
              renderValue={selected => (
                <Box display="flex" alignItems="center">
                  <span style={{ display: 'inline-block', width: 24, height: 24, borderRadius: 4, backgroundColor: selected, marginRight: 10, border: '1px solid #bbb' }} />
                  <span style={{ fontFamily: 'monospace', fontSize: 15 }}>{selected}</span>
                </Box>
              )}
            >
              {COLOR_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>
                  <Box display="flex" alignItems="center">
                    <span style={{ display: 'inline-block', width: 24, height: 24, borderRadius: 4, backgroundColor: opt, marginRight: 10, border: '1px solid #bbb' }} />
                    <span style={{ fontFamily: 'monospace', fontSize: 15 }}>{opt}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Grid container spacing={2}>
          {rows.map((row, idx) => {
            // Prevent same planet selection in a row
            const planet1Options = PLANETS;
            const planet2Options = PLANETS.filter(p => p !== row.planet1);
            const label = idx === 0 ? 'From' : 'To';
            return (
              <Grid item xs={12} key={idx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography sx={{ fontWeight: 500, minWidth: LABEL_COLUMN_WIDTH }}>Planet</Typography>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select
                      value={row.planet1}
                      onChange={e => handleRowChange(idx, 'planet1', e.target.value)}
                      disabled={idx === 1 && !isDifferentPair}
                    >
                      {planet1Options.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Typography sx={{ fontWeight: 500, minWidth: 48, ml: 1 }}>Planet</Typography>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select
                      value={row.planet2}
                      onChange={e => handleRowChange(idx, 'planet2', e.target.value)}
                      disabled={idx === 1 && !isDifferentPair}
                    >
                      {planet2Options.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Typography sx={{ fontWeight: 500, minWidth: 32, ml: 2 }}>{label}</Typography>
                  <OutlinedInput
                    type="number"
                    value={row.from}
                    onChange={e => {
                      let val = e.target.value;
                      // Only allow valid float numbers
                      if (/^\d*\.?\d{0,2}$/.test(val) || val === '') {
                        let num = parseFloat(val);
                        if (isNaN(num)) num = '';
                        else if (num < 0) num = 0;
                        else if (num > 359.99) num = 359.99;
                        handleRowChange(idx, 'from', num === '' ? '' : num);
                      }
                    }}
                    sx={{ width: 100, ml: 0.5, height: 40, fontSize: 16, '& input': { textAlign: 'center', padding: '10px 8px' } }}
                    inputProps={{ min: 0, max: 359.99, step: 0.01, inputMode: 'decimal', pattern: '[0-9]*' }}
                  />
                </Box>
              </Grid>
            );
          })}

          {/*
            Planet Pair — decides whether the window's two ends share a pair of bodies.

            Placed below both rows because it governs the relationship between them rather than
            belonging to either, and because reading it after the rows matches what it says: these
            two pairs are the same, or they are not.
          */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 500, minWidth: LABEL_COLUMN_WIDTH }}>Planet Pair</Typography>
              <RadioGroup
                row
                sx={RADIO_GROUP_SX}
                value={pairMode}
                onChange={e => handlePairModeChange(e.target.value)}
              >
                <FormControlLabel value="same" control={<Radio size="small" />} label="Same" />
                <FormControlLabel value="different" control={<Radio size="small" />} label="Different" />
              </RadioGroup>
            </Box>
          </Grid>

          {/*
            Display Style — how the matched windows are drawn, and nothing else.

            Sits on its own row under Planet Pair, sharing the same label column, so the two radio
            groups and the fields above them all start at the same x.
          */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 500, minWidth: LABEL_COLUMN_WIDTH }}>Display Style</Typography>
              <RadioGroup row sx={RADIO_GROUP_SX} value={displayStyle} onChange={e => setDisplayStyle(e.target.value)}>
                <FormControlLabel value="bands" control={<Radio size="small" />} label="Vertical Bands" />
                <FormControlLabel value="lines" control={<Radio size="small" />} label="Lines Only" />
              </RadioGroup>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
          <Button
            variant="contained"
            color="error"
            sx={{ fontWeight: 600, borderRadius: 2, boxShadow: 'none', minWidth: 90 }}
            onClick={onClose}
          >
            CLOSE
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ px: 4, fontWeight: 'bold', fontSize: 15 }}
            onClick={handleRun}
          >
            RUN
          </Button>
      </DialogActions>
    </Dialog>
  );
}

AspectsAreaHighlightDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRun: PropTypes.func,
  barData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
    symbol: PropTypes.string,
  }).isRequired,
  extendedBarData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
    symbol: PropTypes.string,
  }).isRequired,
  onJournalAppend: PropTypes.func,
};

export default AspectsAreaHighlightDialog;
